/**
 * AccessibilityToolbar.js
 *
 * Widget dostępności zgodny z WCAG 2.1 AA i Ustawą o dostępności cyfrowej.
 * Umożliwia:
 *  - zmniejszenie / reset / powiększenie czcionki (aria-pressed, disabled)
 *  - szerokie odstępy liter (aria-pressed)
 *
 * Kontrolki są zgrupowane i nazwane przez landmark <section aria-label>
 * (region), dzięki czemu całość treści mieści się w landmarku, a każdy
 * przycisk pozostaje osobnym przystankiem Tab.
 * Zmiany stanu są ogłaszane przez live region (role="status").
 *
 * Wymaga AccessibilityProvider w drzewie komponentów.
 */

import React from "react"
import { useAccessibility, FONT_SIZE_LEVELS } from "../../context/AccessibilityContext"

const SIZE_LABELS = {
  normal:  "normalny",
  large:   "duży",
  largest: "największy",
}

export default function AccessibilityToolbar() {
  const {
    fontSizeLevel,
    letterSpacing,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    toggleLetterSpacing,
  } = useAccessibility()

  const fontIdx    = FONT_SIZE_LEVELS.indexOf(fontSizeLevel)
  const isSmallest = fontIdx === 0
  const isLargest  = fontIdx === FONT_SIZE_LEVELS.length - 1

  let srStatus = ""
  if (fontSizeLevel !== "normal") srStatus += `Rozmiar czcionki: ${SIZE_LABELS[fontSizeLevel]}. `
  if (letterSpacing)              srStatus += "Szerokie odstępy liter włączone."

  return (
    <section
      className="m-a11y-toolbar-region"
      aria-label="Ustawienia dostępności"
    >
      <div className="m-a11y-toolbar">
      <span className="m-a11y-toolbar__label" aria-hidden="true">
        Dostępność:
      </span>

      {/* ── Zmniejsz czcionkę ── */}
      <button
        type="button"
        className="m-a11y-toolbar__btn m-a11y-toolbar__btn--a-sm"
        onClick={decreaseFontSize}
        disabled={isSmallest}
        aria-label="Zmniejsz rozmiar czcionki"
        aria-disabled={isSmallest}
        title="Zmniejsz czcionkę"
      >
        <span aria-hidden="true">A-</span>
      </button>

      {/* ── Reset czcionki ── */}
      <button
        type="button"
        className={`m-a11y-toolbar__btn m-a11y-toolbar__btn--a-md${
          fontSizeLevel === "normal" ? " is-active" : ""
        }`}
        onClick={resetFontSize}
        aria-pressed={fontSizeLevel === "normal"}
        aria-label="Przywróć domyślny rozmiar czcionki"
        title="Domyślna czcionka"
      >
        <span aria-hidden="true">A</span>
      </button>

      {/* ── Powiększ czcionkę ── */}
      <button
        type="button"
        className="m-a11y-toolbar__btn m-a11y-toolbar__btn--a-lg"
        onClick={increaseFontSize}
        disabled={isLargest}
        aria-label="Powiększ rozmiar czcionki"
        aria-disabled={isLargest}
        title="Powiększ czcionkę"
      >
        <span aria-hidden="true">A+</span>
      </button>

      {/* ── Live region dla czytników ekranu ── */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {srStatus}
      </div>
      </div>
    </section>
  )
}
